import Promise, { ICancelablePromise } from '@ms/odsp-utilities/lib/async/Promise';
import { Qos as QosEvent, ResultTypeEnum as QosResultEnum } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import { DataRequestor } from '@ms/odsp-graph/lib/base/dataRequestor/DataRequestor';
import DataStore from '@ms/odsp-utilities/lib/models/store/BaseDataStore';
import DataStoreCachingType from '@ms/odsp-utilities/lib/models/store/DataStoreCachingType';
import { ISpPageContext } from '../../interfaces/ISpPageContext';
import { IOAuthTokenProvider } from '../../providers/oAuthToken/OAuthTokenProvider';
import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import { ABExperiment } from '@ms/odsp-utilities/lib/logging/ABExperiment';

const STORE_KEY: string = 'PeoplePicker';
const DISABLE_SUBSTRATE_KEY: string = 'DisableSubstrateSearch';
export const SUBSTRATE_SEARCH_URL: string = 'https://outlook.office.com/search';

export const PeoplePickerSubstrate = { ODB: 1286 };
export const DisablePeoplePickerSubstrateExperiment = { ODB: 1287 };

export interface ISubstratePeoplePickerHelperDependencies {
  oAuthTokenProvider: IOAuthTokenProvider;
  pageContext: ISpPageContext;
}

export interface ISubstratePeoplePickerHelper {
  /**
   * Returns whether or not we are able to use 3S for people search.
   */
  canUseSubstrate(): boolean;

  /**
   * Disables 3S calls for subsequent picker actions.
   */
  disableSubstrateCallsForPicker();

  /**
   * Retrieves an access token for calling MS 3S.
   */
  getSubstrateToken(): ICancelablePromise<string>;

  /**
   * Call 3S Init to warm up the 3S search enginge
   */
  init(token: string): void;
}

export class SubstratePeoplePickerHelper implements ISubstratePeoplePickerHelper {
  private _dataRequestor: DataRequestor;
  private _dataStore: DataStore;
  private _pageContext: ISpPageContext;
  private _oAuthTokenProvider: IOAuthTokenProvider;

  constructor(params: {}, dependencies: ISubstratePeoplePickerHelperDependencies) {
    this._dataRequestor = new DataRequestor({}, {});
    this._pageContext = dependencies.pageContext;
    this._oAuthTokenProvider = dependencies.oAuthTokenProvider;
    this.initializeDataStore();
  }

  /**
   * Returns whether or not we are able to use 3S for people search. 3S calls can be disabled if the OAuth call
   * previously failed, if we are token authenticated (Acquire only supports cookie auth), or if the user isn't in the
   * A/B experiment to use 3S.
   */
  public canUseSubstrate(): boolean {
    let canUseSubstrate: boolean = false;

    // We can't call 3S On-Prem
    if (this._pageContext && !this._pageContext.isSPO) {
      return canUseSubstrate;
    }
    // Disable 3S if we're authenticated with an access token. Currently, 3S calls are only supported with cookie auth.
    if (this._pageContext && !!this._pageContext.authToken) {
      return canUseSubstrate;
    }
    if (this._pageContext && this._oAuthTokenProvider && FeatureOverrides.isFeatureEnabled(PeoplePickerSubstrate)) {
      canUseSubstrate = true;
    }

    if (this._dataStore) {
      canUseSubstrate = canUseSubstrate && !this._dataStore.getValue<boolean>(DISABLE_SUBSTRATE_KEY, DataStoreCachingType.session);
    }

    if (canUseSubstrate) {
      canUseSubstrate = canUseSubstrate && this.isInExperimentToUseSubstrate();
    }
    return canUseSubstrate;
  }

  /**
   * Store flag to disable 3S calls in session storage so we don't keep making OAuth token calls that will error.
   * The calls are expensive and if the user is unable to call 3S we shouldn't keep trying. Since some errors are
   * recoverable outside the session, don't scope to something as long-lived as local storage.
   */
  public disableSubstrateCallsForPicker() {
    this.initializeDataStore();
    this._dataStore.setValue<boolean>(DISABLE_SUBSTRATE_KEY, true, DataStoreCachingType.session);
  }

  /**
   * Retrieves an access token for calling 3S.
   */
  public getSubstrateToken(): ICancelablePromise<string> {
    if (this._oAuthTokenProvider) {
      /**
       * OAuthTokenProvider already logs QOS events for its calls but we want to measure retrieving 3S tokens from within the people picker
       * separately. This allows us to track how often users are forced into the fallback logic scenario.
       */
      const qos = new QosEvent({ name: "PeoplePicker.GetSubstrateToken" });
      return this._oAuthTokenProvider.getToken(SUBSTRATE_SEARCH_URL).then((oAuthToken: string) => {
        qos.end({
          resultType: QosResultEnum.Success
        });

        return oAuthToken;
      }, (error: Error) => {
        if (Promise.isCanceled(error)) {
          qos.end({
            resultType: QosResultEnum.ExpectedFailure,
            resultCode: 'Canceled'
          });
          return Promise.reject(error);
        } else {
          qos.end({
            resultCode: "PeoplePicker.GetSubstrateTokenError", resultType: QosResultEnum.Failure, extraData: {
              serverErrorCode: error["_serverErrorCode"],
              message: error.message
            }
          });
          // If the Acquire call fails, disable future attempts to retrieve the 3S token for the current session.
          this.disableSubstrateCallsForPicker();
        }
        return null;
      });
    }

    return null;
  }

  public init(oAuthToken: string) {
    const scenario = 'documentshare';
    const initUrl = `${SUBSTRATE_SEARCH_URL}/api/v1/init?scenario=${scenario}`;
    this._dataRequestor.send({
      apiName: 'Substrate.PeopleSearch.Init',
      path: initUrl,
      headers: {
        Authorization: 'Bearer ' + oAuthToken,
        'X-User-Context': 'litoken=' + oAuthToken
      },
      needsAuthorization: false
    });
  }

  private initializeDataStore() {
    if (!this._dataStore) {
      this._dataStore = new DataStore(STORE_KEY, DataStoreCachingType.session);
    }
  }

  /**
   * Put the 3S calls behind an A/B experiment so we can determine what kind of impact the smart people picker has on sharing overall.
  */
  private isInExperimentToUseSubstrate(): boolean {
    const shouldExperiment = !FeatureOverrides.isFeatureEnabled(DisablePeoplePickerSubstrateExperiment);
    if (shouldExperiment) {
      const experiment: ABExperiment = new ABExperiment({
        name: "UseSubstratePeoplePicker",
        startDate: "10/30/2018"
      });
      return experiment.IsExperimentOn();
    }
    return true;
  }
}